import { SxProps, TextField, TextFieldProps, Theme, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { ChangeEvent, memo, useCallback, useEffect, useState } from "react";
import { useStyles } from "./styles";

export type SlTargetType = 'points' | 'percentage' | 'mtm' | 'price'

export type SlTargetInputField = {
    height?: number,
    width?: number,
    minValue?: number,
    maxValue?: number,
    valueType?: SlTargetType,
    onValueChange: (value: number, valueType: SlTargetType) => void
} & TextFieldProps

const SlTargetInputField: React.FC<SlTargetInputField> = ({height, width, minValue, maxValue, onValueChange, valueType, onBlur, onFocus, ...inputProps}) => {
    const { classes } = useStyles();
    let inputFieldProps = {...(inputProps || {})};

    const value = inputProps.value as number;
    const [focused, setFocused] = useState(false);
    const [displayValue, setDisplayValue] = useState(() => String(value ?? ''));

    useEffect(() => {
        if (!focused) {
            setDisplayValue(String(value ?? ''));
        }
    }, [value, focused]);
    let inputSxProps: SxProps = inputProps.sx || {
        textAlign: "center"
    };

    if (height) {
        inputSxProps = {
            ...inputSxProps,
            height: `${height}px`,
            paddingY: 0,
            lineHeight: `${height}px`
        }
    }

    if (width) {
        inputSxProps = {
            ...inputSxProps,
            width: `${width}px`
        }
    }

    const pointsSelected = valueType === "points";

    const onValueTypeClick = useCallback((event: React.MouseEvent<HTMLElement, MouseEvent>, newValueType: SlTargetType) => {
        onValueChange(value, newValueType);
    }, [value, onValueChange]);
    const onInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        const rawValue = event.target.value;
        setDisplayValue(rawValue);
        if (rawValue === '' || isNaN(Number(rawValue))) return;

        const newValue = Number(rawValue);
        if ((minValue !== undefined && newValue < minValue) || (maxValue !== undefined && newValue > maxValue)) return;
        onValueChange(newValue, valueType);
    }, [valueType, onValueChange, minValue, maxValue])

    const handleFocus = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
        setFocused(true);
        onFocus?.(event);
    }, [onFocus])

    const handleBlur = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
        setFocused(false);
        const rawValue = event.target.value;
        const numValue = rawValue === '' || isNaN(Number(rawValue)) ? 0 : Number(rawValue);
        setDisplayValue(String(numValue));
        if (numValue !== value) {
            onValueChange(numValue, valueType);
        }
        onBlur?.(event);
    }, [value, valueType, onValueChange, onBlur])


    inputFieldProps = {
        ...inputFieldProps,
        InputProps: {
            ...(inputFieldProps.InputProps || {}),
            endAdornment: <ToggleButtonGroup
                id={'option-chain-quik-view-group'}
                size="small"
                value={valueType}
                sx={{height: height ? `${height - 8}px` : 'auto'}}
            >
                <ToggleButton selected={pointsSelected} value="points" onClick={onValueTypeClick} sx={{ color: (theme) => pointsSelected ? theme.palette.text.primary : theme.palette.text.secondary }}>
                    Pts
                </ToggleButton>
                <ToggleButton selected={!pointsSelected} value="percentage" onClick={onValueTypeClick} sx={{ color: (theme) => pointsSelected ? theme.palette.text.primary : theme.palette.text.secondary }}>
                    %
                </ToggleButton>
            </ToggleButtonGroup>
            }
    }
    
    inputFieldProps.inputProps = {
        ...(inputFieldProps.inputProps || {}),
        sx: inputSxProps
    }
    return <TextField value={displayValue} {...inputFieldProps} sx={{paddingX: 0}} onChange={onInputChange} onFocus={handleFocus} onBlur={handleBlur} classes={{root: classes.inputRoot}} />;
}

export default memo(SlTargetInputField)